import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import BooleanBadge from 'src/features/membership/components/BooleanBadge';
import BackButton from 'src/features/navigation/components/BackButton';
import { PATH } from 'src/features/navigation/utils/constants';
import { useGetLinkedEntities } from 'src/features/pricingPlan/api/linkedEntities/useGetLinkedEntities';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import CopyPricingPlanFormModal from 'src/features/pricingPlan/components/form/CopyPricingPlanFormModal';
import CreatePricingPlanVersionFormModal from 'src/features/pricingPlan/components/form/CreatePricingPlanVersionFormModal';
import DeletePricingPlanFormModal from 'src/features/pricingPlan/components/form/DeletePricingPlanFormModal';
import UpdatePricingPlanFormModal from 'src/features/pricingPlan/components/form/UpdatePricingPlanFormModal';
import FormattedTimeStat from 'src/features/pricingPlan/components/FormattedTimeStat';
import PreviousNextVersionLink from 'src/features/pricingPlan/components/PreviousNextVersionLink';
import PricingPlanBadge from 'src/features/pricingPlan/components/PricingPlanBadge';
import PricingPlanDashboardTables from 'src/features/pricingPlan/components/PricingPlanDashboardTables';
import PricingPlanUsedInBadge from 'src/features/pricingPlan/components/PricingPlanUsedInBadge';
import PublishPlanModal from 'src/features/pricingPlan/components/PublishPlanModal';
import VersionStatusBadge from 'src/features/pricingPlan/components/VersionStatusBadge';
import {
    cappingTranslationMapping,
    freeTimeTypeMapping,
    linkedEntitiesTranslationMap,
    pricingBillingBadgeOptions,
    pricingCalculationBadgeOptions,
    pricingStatusBadgeOptions,
    tickTypeTranslationMapping,
} from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { CALCULATION_TYPE, LINKED_ENTITIES_KEYS, PRICING_PLAN_STATUS } from 'src/features/pricingPlan/utils/types';
import DashboardGeneralBlock from 'src/features/stay/components/dashboard/DashboardGeneralBlock';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import { generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { commonMessages } from 'src/utils/messages';

const PricingPlanDashboard = () => {
    const { formatMessage, locale } = useIntl();
    const { planId = '' } = useParams();
    const navigate = useNavigate();
    const { parkingId } = useParking();

    const managePricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);

    const { setTitle, removeTitle } = useTitleStore();

    const [isUsedInSelected, setIsUsedInSelected] = React.useState<boolean>(false);

    React.useEffect(() => {
        setTitle(formatMessage(pricingPlanMessages.pricingPlanDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const {
        data: pricingPlanData,
        isLoading,
        isError,
    } = useGetPricingPlan({
        planId,
        parkingId,
        config: { enabled: !!planId },
    });

    const { data: linkedEntities } = useGetLinkedEntities({
        pricingPlanId: planId,
        parkingId,
        config: { enabled: !!planId },
    });

    // Walk the known section keys instead of the response's key order: the key is what the badge
    // label is looked up by, and reading it back out of the section object breaks the moment a
    // section grows a second field (formatMessage would then be called with an undefined id).
    const usedInSections = React.useMemo(
        () =>
            Object.values(LINKED_ENTITIES_KEYS)
                .map((key) => ({ key, totalCount: linkedEntities?.[key]?.total_count ?? 0 }))
                .filter((section) => section.totalCount > 0),
        [linkedEntities],
    );

    if (isLoading || isError || !pricingPlanData) return null;

    const isPerFixedTick = pricingPlanData?.calculation_type === CALCULATION_TYPE.PER_FIXED_TICK;

    const isPerFixedPrice = pricingPlanData?.calculation_type === CALCULATION_TYPE.FIXED_PRICE;

    const isSystem = !!pricingPlanData?.is_system;

    const showDeleteAction = pricingPlanData?.status === PRICING_PLAN_STATUS.DRAFT && !isSystem;

    const pricingPlanName =
        pricingPlanData?.translations?.find((plan) => plan.language === locale)?.name ??
        formatMessage(pricingPlanMessages.pricingPlan);

    const fixedRate = pricingPlanData?.fixed_rate
        ? Number(pricingPlanData.fixed_rate.rate_with_tax / 100).toFixed(2)
        : '-';

    const taxPercent = pricingPlanData?.tax_percent
        ? `${(Number(pricingPlanData?.tax_percent) * 100).toFixed(2)}%`
        : '-';

    const freeTimeTypeDisplay = pricingPlanData?.free_time_type
        ? formatMessage(freeTimeTypeMapping[pricingPlanData?.free_time_type])
        : '-';

    const cappingDisplay = pricingPlanData?.capping
        ? formatMessage(cappingTranslationMapping[pricingPlanData?.capping])
        : '-';

    const tickTypeDisplay = pricingPlanData?.tick?.tick_type
        ? formatMessage(tickTypeTranslationMapping[pricingPlanData?.tick?.tick_type])
        : '-';

    const hasLinkedEntities = Boolean(linkedEntities && linkedEntities?.total_entities_count > 0);
    const description = pricingPlanData?.translations?.find((des) => des.language === locale)?.description;

    const dashboardBlockData = {
        section1: [
            {
                title: pricingPlanMessages.billingType,
                value: (
                    <PricingPlanBadge
                        badgeOptions={pricingBillingBadgeOptions}
                        badgeIndexKey={pricingPlanData?.billing_type}
                    />
                ),
            },
            {
                title: pricingPlanMessages.calculationType,
                value: (
                    <PricingPlanBadge
                        badgeOptions={pricingCalculationBadgeOptions}
                        badgeIndexKey={pricingPlanData?.calculation_type}
                    />
                ),
            },
            ...(isPerFixedTick
                ? [
                      {
                          title: pricingPlanMessages.tickType,
                          value: tickTypeDisplay,
                      },
                      {
                          title: pricingPlanMessages.tickDuration,
                          value: <FormattedTimeStat totalMinutes={pricingPlanData?.tick?.tick_minutes} />,
                      },
                  ]
                : isPerFixedPrice
                  ? [
                        {
                            title: pricingPlanMessages.rateWithTax,
                            value: fixedRate,
                        },
                    ]
                  : [
                        {
                            title: pricingPlanMessages.capping,
                            value: cappingDisplay,
                        },
                    ]),
            {
                title: pricingPlanMessages.status,
                value: (
                    <PricingPlanBadge
                        badgeOptions={pricingStatusBadgeOptions}
                        badgeIndexKey={pricingPlanData?.status}
                    />
                ),
            },
            {
                title: pricingPlanMessages.isSystem,
                value: <BooleanBadge booleanField={pricingPlanData?.is_system} />,
            },
        ],
        section2: [
            {
                title: commonMessages.currency,
                value: pricingPlanData?.currency,
            },
            {
                title: pricingPlanMessages.taxPercent,
                value: taxPercent,
            },
        ],
        section3: [
            {
                title: pricingPlanMessages.freeTimeType,
                value: freeTimeTypeDisplay,
            },
            {
                title: pricingPlanMessages.freeTime,
                value: <FormattedTimeStat totalMinutes={pricingPlanData?.free_time_minutes} />,
            },
            {
                title: pricingPlanMessages.freeTimeCap,
                value: <FormattedTimeStat totalMinutes={pricingPlanData?.free_time_cap_minutes} />,
            },
            {
                title: pricingPlanMessages.exitTime,
                value: <FormattedTimeStat totalMinutes={pricingPlanData?.exit_time} />,
            },
            {
                title: pricingPlanMessages.exitTimeGrace,
                value: <FormattedTimeStat totalMinutes={pricingPlanData?.exit_time_grace} />,
            },
            {
                title: pricingPlanMessages.extraFreeTime,
                value: <BooleanBadge booleanField={Boolean(pricingPlanData?.extra_free_time)} />,
            },
            {
                title: pricingPlanMessages.extraFreeTimeMinutes,
                value: <FormattedTimeStat totalMinutes={pricingPlanData?.extra_free_time_minutes} />,
            },
            {
                title: pricingPlanMessages.extraFreeTimeActivationRemainingMinutes,
                value: (
                    <FormattedTimeStat totalMinutes={pricingPlanData?.extra_free_time_activation_remaining_minutes} />
                ),
            },
        ],
        section4: [
            {
                title: pricingPlanMessages.validFromPricing,
                value: getFormattedDateTimeForDetailPanel(pricingPlanData?.valid_from),
            },
            {
                title: pricingPlanMessages.validToPricing,
                value: getFormattedDateTimeForDetailPanel(pricingPlanData?.valid_to),
            },
            {
                title: pricingPlanMessages.previousVersion,
                value: <PreviousNextVersionLink version={pricingPlanData?.previous_version} />,
            },
            {
                title: pricingPlanMessages.nextVersion,
                value: <PreviousNextVersionLink version={pricingPlanData?.next_version} />,
            },
        ],
        section5: [
            {
                title: pricingPlanMessages.description,
                value: description && description !== '' ? description : '-',
            },
        ],
        ...(hasLinkedEntities
            ? {
                  section6: [
                      {
                          title: pricingPlanMessages.usedIn,
                          value: (
                              <div className="flex flex-row flex-wrap gap-2">
                                  {usedInSections.map(({ key, totalCount }) => (
                                      <PricingPlanUsedInBadge
                                          badgeTitle={formatMessage(linkedEntitiesTranslationMap[key], {
                                              value: totalCount,
                                          })}
                                          innerBadgeTitle={String(totalCount)}
                                          key={key}
                                      />
                                  ))}
                              </div>
                          ),
                      },
                      {
                          value: (
                              <div className="flex w-full flex-1 justify-start md:justify-end">
                                  <Button
                                      onClick={() => setIsUsedInSelected(true)}
                                      variant="link"
                                      className="text-primary-700 flex cursor-pointer flex-row items-center gap-1 text-sm font-medium"
                                  >
                                      {formatMessage(pricingPlanMessages.viewAllRecords, {
                                          count: linkedEntities?.total_entities_count ?? 0,
                                      })}
                                  </Button>
                              </div>
                          ),
                      },
                  ],
              }
            : {}),
    };

    return (
        <>
            <BackButton customRoute={`/${PATH.ADMINISTRATION}/pricing-plan`} />
            <SectionBlock>
                <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                    <SectionBlockHead>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">{pricingPlanName}</SectionBlockTitle>
                        <SectionBlockSubtitle>
                            <VersionStatusBadge
                                isCurrentVersion={pricingPlanData?.is_current_version}
                                validFrom={pricingPlanData?.valid_from}
                            />
                        </SectionBlockSubtitle>
                    </SectionBlockHead>

                    <div className="flex flex-wrap gap-2">
                        <Button asChild size="mdIcon" variant="outline">
                            <Link
                                to={`/${PATH.ADMINISTRATION}/pricing-plan/${pricingPlanData?.id}/${pricingPlanData?.identity}`}
                            >
                                <div className="block md:hidden">
                                    <FontAwesomeIcon icon={ICON_MAP.TABLE_LIST} className="opacity-50" />
                                </div>
                                <span className="hidden md:block">
                                    {formatMessage(pricingPlanMessages.allVersions)}
                                </span>
                            </Link>
                        </Button>

                        {/* A fixed price plan prices memberships, not stays - there is nothing to simulate */}
                        {!isPerFixedPrice && (
                            <Button asChild size="mdIcon" variant="outline">
                                <Link to={`/${PATH.ADMINISTRATION}/pricing-plan/${pricingPlanData?.id}/simulate`}>
                                    <div className="block md:hidden">
                                        <FontAwesomeIcon icon={ICON_MAP.CALCULATOR} className="opacity-50" />
                                    </div>
                                    <span className="hidden md:block">
                                        {formatMessage(pricingPlanMessages.simulatePricing)}
                                    </span>
                                </Link>
                            </Button>
                        )}

                        {managePricingPlans && !isSystem && (
                            <UpdatePricingPlanFormModal
                                selectedPricingPlan={pricingPlanData}
                                semantics={{
                                    entryName: formatMessage(pricingPlanMessages.pricingPlan).toLowerCase(),
                                }}
                            >
                                <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                    <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                                </Button>
                            </UpdatePricingPlanFormModal>
                        )}

                        {managePricingPlans && showDeleteAction && (
                            <DeletePricingPlanFormModal
                                selectedPricingPlan={pricingPlanData}
                                semantics={{
                                    entryName: formatMessage(pricingPlanMessages.pricingPlan).toLowerCase(),
                                }}
                                onDeleted={() => void navigate(`/${PATH.ADMINISTRATION}/pricing-plan`)}
                            >
                                <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                    <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                </Button>
                            </DeletePricingPlanFormModal>
                        )}

                        {managePricingPlans && pricingPlanData && (
                            <CopyPricingPlanFormModal
                                selectedPricingPlan={pricingPlanData}
                                semantics={{
                                    entryName: formatMessage(pricingPlanMessages.pricingPlan).toLowerCase(),
                                }}
                            >
                                <Button variant="default" size="mdIcon" className="cursor-pointer">
                                    <FontAwesomeIcon icon={ICON_MAP.COPY} />
                                    <span className="hidden md:block">{formatMessage(generalMessages.copy)}</span>
                                </Button>
                            </CopyPricingPlanFormModal>
                        )}

                        {managePricingPlans && !isSystem && (
                            <CreatePricingPlanVersionFormModal
                                selectedPricingPlan={pricingPlanData}
                                semantics={{
                                    entryName: formatMessage(pricingPlanMessages.version).toLowerCase(),
                                }}
                            />
                        )}

                        {managePricingPlans &&
                            !isSystem &&
                            pricingPlanData &&
                            pricingPlanData.status === PRICING_PLAN_STATUS.DRAFT && (
                                <PublishPlanModal plan={pricingPlanData}>
                                    <Button size="mdIcon" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.PUBLISH} />
                                        <span className="hidden md:block">
                                            {formatMessage(pricingPlanMessages.publish)}
                                        </span>
                                    </Button>
                                </PublishPlanModal>
                            )}
                    </div>
                </SectionBlockHeader>

                <GeneralBlock asChild>
                    <DashboardGeneralBlock
                        dashboardData={dashboardBlockData}
                        indexForLastSeparator={hasLinkedEntities ? 5 : 4}
                        sectionClassName={hasLinkedEntities ? 'xl:grid-cols-2 2xl:grid-cols-2' : undefined}
                    />
                </GeneralBlock>
            </SectionBlock>

            <PricingPlanDashboardTables
                isPerFixedTick={isPerFixedTick}
                isPerFixedPrice={isPerFixedPrice}
                currency={pricingPlanData?.currency}
                pricingPlanIdentity={pricingPlanData?.id}
                isUsedInSelected={isUsedInSelected}
                setIsUsedInSelected={setIsUsedInSelected}
            />
        </>
    );
};

export default PricingPlanDashboard;
